import React, { useCallback, useContext, useMemo } from 'react';
import { Outlet, Route, Routes, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import translations from './evaluationtabs.i18n';
import { LearningGoalsContainer } from './learninggoals';
import { DailyEvaluationContainer } from './dailyevaluation';
import { ConfirmationContainer } from './confirmation';
import { AbsenceContainer } from './absence';
import { Tab, Tabs } from '../components/tabs/tabs';
import PageLayout from '../pagelayout';
import { BackButton } from '../pagelayout/backButton';
import { UserType } from '../util/userType';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import {
  MinimalAktivitetInfoDto as MinimalAktivitetUHDto,
  PraksisaktivitetDto as UHAktivitetDto,
} from '../api/generated/studiested-generated-api';
import {
  MinimalAktivitetInfoDto as MinimalAktivitetPSDto,
  PraksisaktivitetDto as PSAktivitetDto,
} from '../api/generated/praksissted-generated-api';
import { useLoader } from '../api/useLoader';
import Spinner from '../components/spinner/spinner';
import EvaluationStudentTableWrapper from './evaluationstudenttable';

interface EvaluationTabsWrapperProps {
  userType: UserType;
}

export enum EvaluationType {
  MIDTERM,
  FINAL,
}

type SessionActivity =
  | MinimalAktivitetUHDto
  | MinimalAktivitetPSDto
  | undefined;

const EvaluationTabsWrapper = ({ userType }: EvaluationTabsWrapperProps) => {
  const { formatMessage } = useIntl();
  const { activityId } = useParams<{ activityId: string }>();

  const sessionActivity: SessionActivity = useMemo(() => {
    if (activityId) {
      return JSON.parse(sessionStorage.evaluationActivities)[activityId];
    }
  }, [activityId]);

  if (sessionActivity) {
    return (
      <EvaluationTabsPage
        title={`${formatMessage({ id: translations.title.id })} ${
          sessionActivity.emnekode
        } - ${sessionActivity.praksistypenavn}`}
        loading={false}
        userType={userType}
      />
    );
  }

  if (activityId) {
    return (
      <EvaluationTabsPageWithoutStoredActivity
        userType={userType}
        activityId={activityId}
      />
    );
  }

  return <></>;
};

interface EvaluationTabsPageWithoutStoredActivityProps
  extends EvaluationTabsWrapperProps {
  activityId: string;
}

const EvaluationTabsPageWithoutStoredActivity: React.FC<
  React.PropsWithChildren<EvaluationTabsPageWithoutStoredActivityProps>
> = ({ userType, activityId }) => {
  const { formatMessage } = useIntl();
  const { praksiskalenderApi: UHKalenderApi } =
    useContext(StudiestedApiContext);
  const { praksiskalenderApi: PSKalenderApi } = useContext(
    PraksisstedApiContext,
  );
  const { data, loading } = useLoader<UHAktivitetDto | PSAktivitetDto>(
    () =>
      (userType === UserType.UH
        ? UHKalenderApi
        : PSKalenderApi
      ).praksisaktivitetIdGet({
        pathParams: { id: activityId! },
      }),
    [activityId],
  );

  const labels = useMemo(
    () => ({
      title: formatMessage({ id: translations.title.id }),
      loading_title: formatMessage({ id: translations.loading_title.id }),
    }),
    [formatMessage],
  );

  const formatEmneAndPraksis = useCallback(() => {
    const uhData = data as UHAktivitetDto | undefined;
    const psData = data as PSAktivitetDto | undefined;

    return uhData?.emne?.kode
      ? `${uhData.emne.kode} - ${psData?.praksis.navn}`
      : `${psData?.praksis.navn}`;
  }, [data]);

  return (
    <EvaluationTabsPage
      title={
        loading
          ? labels.loading_title
          : `${labels.title} ${formatEmneAndPraksis()}`
      }
      loading={loading}
      userType={userType}
    />
  );
};

interface EvaluationTabsPageProps extends EvaluationTabsWrapperProps {
  loading: boolean;
  title: string;
}

const EvaluationTabsPage: React.FC<
  React.PropsWithChildren<EvaluationTabsPageProps>
> = ({ loading, title, userType }) => {
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      back: formatMessage({ id: translations.back.id }),
    }),
    [formatMessage],
  );

  const actionItems = <BackButton title={labels.back} path={'/evaluation'} />;

  return (
    <PageLayout title={title} actionItems={actionItems} isIndented>
      <EvaluationTabs loading={loading} userType={userType} />
    </PageLayout>
  );
};

const EvaluationTabsPaths = {
  daily_evaluation: 'daily',
  midterm_evaluation: 'midterm',
  final_evaluation: 'endterm',
  absence: 'absence',
  confirmation: 'confirmation',
} as const;

interface EvaluationTabsProps extends EvaluationTabsWrapperProps {
  loading: boolean;
}

const EvaluationTabs: React.FC<
  React.PropsWithChildren<EvaluationTabsProps>
> = ({ loading, userType }) => {
  const { formatMessage } = useIntl();

  const labels = useMemo(
    () => ({
      learning_goals: formatMessage({ id: translations.learning_goals.id }),
      daily_evaluation: formatMessage({ id: translations.daily_evaluation.id }),
      mid_term_evaluation: formatMessage({
        id: translations.mid_term_evaluation.id,
      }),
      final_evaluation: formatMessage({ id: translations.final_evaluation.id }),
      absence: formatMessage({ id: translations.absence.id }),
      confirmation: formatMessage({ id: translations.confirmation.id }),
    }),
    [formatMessage],
  );

  if (loading) {
    return <Spinner height={'3em'} />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <>
            <Tabs>
              <Tab to="" end>
                {labels.learning_goals}
              </Tab>
              <Tab to={EvaluationTabsPaths.daily_evaluation}>
                {labels.daily_evaluation}
              </Tab>
              <Tab to={EvaluationTabsPaths.midterm_evaluation}>
                {labels.mid_term_evaluation}
              </Tab>
              <Tab to={EvaluationTabsPaths.final_evaluation}>
                {labels.final_evaluation}
              </Tab>
              <Tab to={EvaluationTabsPaths.absence}>{labels.absence}</Tab>
              <Tab to={EvaluationTabsPaths.confirmation}>
                {labels.confirmation}
              </Tab>
            </Tabs>
            <Outlet />
          </>
        }
      >
        <Route path="" element={<LearningGoalsContainer />} />
        <Route
          path={EvaluationTabsPaths.daily_evaluation}
          element={<DailyEvaluationContainer />}
        />
        <Route
          path={EvaluationTabsPaths.midterm_evaluation}
          element={
            <EvaluationStudentTableWrapper
              evaluationType={EvaluationType.MIDTERM}
              userType={userType}
            />
          }
        />
        <Route
          path={EvaluationTabsPaths.final_evaluation}
          element={
            <EvaluationStudentTableWrapper
              evaluationType={EvaluationType.FINAL}
              userType={userType}
            />
          }
        />
        <Route
          path={EvaluationTabsPaths.absence}
          element={<AbsenceContainer />}
        />
        <Route
          path={EvaluationTabsPaths.confirmation}
          element={<ConfirmationContainer />}
        />
      </Route>
    </Routes>
  );
};

export default EvaluationTabsWrapper;
